import {useState, useCallback, useEffect, useRef} from 'react';
import {initializeGame, moveGrid, addRandomTile, checkGameOver} from '../utils/gameLogic';
import {ACHIEVEMENTS_LIST} from '../utils/constants';
import confetti from 'canvas-confetti';

export const useGame = () => {
  const [grid, setGrid] = useState(initializeGame);
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(() => 
    parseInt(localStorage.getItem('cute2048Best') || '0')
  );
  const [status, setStatus] = useState('playing');
  const [stats, setStats] = useState({
    moves: 0,
    merges: 0,
    maxTile: 2
  });
  const [unlockedTiles, setUnlockedTiles] = useState(() => 
    JSON.parse(localStorage.getItem('cute2048Unlocked') || '{"2":true,"4":true}')
  );
  const [unlockedAchievements, setUnlockedAchievements] = useState(() => 
    JSON.parse(localStorage.getItem('cute2048Achievements') || '{}')
  );
  const [achievementNotifications, setAchievementNotifications] = useState([]);
  const [nextValue, setNextValue] = useState(() => Math.random() < 0.9 ? 2 : 4);

  // 🎯 使用 ref 追蹤已通知的成就,避免重複通知
  const notifiedAchievements = useRef(new Set(Object.keys(unlockedAchievements)));

  const saveProgress = (newBest, newUnlocked, newAchievements) => {
    try {
      localStorage.setItem('cute2048Best', newBest.toString());
      localStorage.setItem('cute2048Unlocked', JSON.stringify(newUnlocked));
      localStorage.setItem('cute2048Achievements', JSON.stringify(newAchievements));
    } catch (e) {
      console.error('Failed to save progress:', e);
    }
  };

  const showAchievementNotification = (achievement) => {
    const notification = {
      id: Date.now() + Math.random(),
      achievement
    };
    setAchievementNotifications(prev => [...prev, notification]);
    
    setTimeout(() => {
      setAchievementNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 3500);
  };

  // 🎯 即時檢查成就(每次遊戲狀態更新時)
  const checkAchievements = useCallback((currentStats, currentMaxTile, currentScore) => {
    const gameState = {
      stats: currentStats,
      maxTile: currentMaxTile,
      score: currentScore,
      moves: currentStats.moves
    };

    ACHIEVEMENTS_LIST.forEach((achievement) => {
      const isUnlocked = achievement.condition(gameState);
      const wasNotified = notifiedAchievements.current.has(achievement.id);

      // 如果成就達成且從未通知過
      if (isUnlocked && !wasNotified) {
        const newUnlockedAchievements = {
          ...unlockedAchievements,
          [achievement.id]: true
        };
        setUnlockedAchievements(newUnlockedAchievements);
        saveProgress(bestScore, unlockedTiles, newUnlockedAchievements);
        showAchievementNotification(achievement);
        
        // 標記為已通知
        notifiedAchievements.current.add(achievement.id);
      }
    });
  }, [unlockedAchievements, bestScore, unlockedTiles]);

  const resetGame = () => {
    setGrid(initializeGame());
    setScore(0);
    setStatus('playing');
    setStats({
      moves: 0,
      merges: 0,
      maxTile: 2
    });
    setNextValue(Math.random() < 0.9 ? 2 : 4);
  };

  const move = useCallback((direction) => {
    if (status !== 'playing' && status !== 'won_continue') return;

    const result = moveGrid(grid, direction);
    
    if (result.moved) {
      const gridWithNewTile = addRandomTile(result.grid, nextValue);
      setGrid(gridWithNewTile);
      setNextValue(Math.random() < 0.9 ? 2 : 4);

      const newScore = score + result.score;
      setScore(newScore);

      if (newScore > bestScore) {
        setBestScore(newScore);
      }

      const flatValues = gridWithNewTile.flat().filter(t => t).map(t => t.value);
      const currentMax = Math.max(...flatValues, 2);

      const newStats = {
        moves: stats.moves + 1,
        merges: stats.merges + result.merges,
        maxTile: Math.max(stats.maxTile, currentMax)
      };
      setStats(newStats);

      // 🎯 即時檢查成就(在這裡觸發)
      checkAchievements(newStats, Math.max(stats.maxTile, currentMax), newScore);

      if (!unlockedTiles[currentMax]) {
        const newUnlocked = {
          ...unlockedTiles,
          [currentMax]: true
        };
        setUnlockedTiles(newUnlocked);
        saveProgress(Math.max(newScore, bestScore), newUnlocked, unlockedAchievements);
      } else {
        saveProgress(Math.max(newScore, bestScore), unlockedTiles, unlockedAchievements);
      }

      if (currentMax >= 2048 && status !== 'won_continue' && !unlockedTiles[2048]) {
        setStatus('won');
        confetti({
          particleCount: 150,
          spread: 70,
          origin: {y: 0.6}
        });
      } else if (checkGameOver(gridWithNewTile)) {
        setStatus('over');
      }
    }
  }, [grid, score, bestScore, status, stats, unlockedTiles, unlockedAchievements, nextValue, checkAchievements]);

  return {
    grid,
    score,
    bestScore,
    status,
    stats,
    unlockedTiles,
    unlockedAchievements,
    achievementNotifications,
    nextValue,
    move,
    resetGame,
    continueGame: () => setStatus('won_continue'),
    setUnlockedAchievements,
    showAchievementNotification,
    saveProgress
  };
};